import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  canHoldVisits,
  qtyToHundredths,
  WORK_ORDER_TASK_STATUSES,
  type Role,
  type WorkOrderAssignLineInput,
  type WorkOrderTaskUpdateInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey, idem } from '../../lib/idempotency';
import { isRetryableMutationRefusal } from '../../lib/refusal';
import { shouldRetryWrite } from '../../lib/write-refusals';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { InlineRename } from '../ui/InlineRename';
import { Field, field } from '../create/Field';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconJobs } from '../icons';
import { AvailabilitySheet, type AvailabilityContext } from './AvailabilitySheet';
import { JobPartsCard } from './JobPartsCard';
import { WorkOrderBackordersCard } from './WorkOrderBackordersCard';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from './CommercialWriteRecovery';

// A refused metadata edit needs a changed draft or an explicit reload, not
// repeated delivery. Keep the existing transport budget and account/busy guards
// for transient failures and lost replies, always with the original intent.
const shouldRetryTaskEdit = (failureCount: number, error: unknown) =>
  isRetryableMutationRefusal(error) && shouldRetryWrite(failureCount, error);

export function WorkOrderTasksCard({
  workOrderId,
  visits,
  timezone,
  legacyClosedReason,
}: {
  workOrderId: string;
  visits: { id: string; scheduledStartAt: Date | string | null; status: string }[];
  timezone: string;
  legacyClosedReason: string | null;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const online = useOnline();
  const mayEdit = can(session.role, 'job', 'update');
  const q = useQuery(trpc.workOrder.get.queryOptions({ workOrderId }));
  const members = useQuery({ ...trpc.org.members.queryOptions(), enabled: mayEdit });
  const [name, setName] = useState('');
  const [owner, setOwner] = useState('');
  const [duration, setDuration] = useState('');
  const [chosenVisits, setChosenVisits] = useState<Record<string, string>>({});
  const [visitMinutes, setVisitMinutes] = useState<Record<string, string>>({});
  // A concurrent assignment can move a line between task sections. Its unsaved
  // intent must survive that component move, still bound to the original version.
  const [lineDrafts, setLineDrafts] = useState<Record<string, LineTaskDraft | null>>({});
  const [availability, setAvailability] = useState<AvailabilityContext | null>(null);
  const editLock = useRef(false);
  const [editing, setEditing] = useState(false);
  const recovery = useCommercialWriteRecovery('workOrder.taskCreate', workOrderId);
  const refresh = () =>
    qc.invalidateQueries({ queryKey: trpc.workOrder.get.queryKey({ workOrderId }) });
  const refreshEdit = () =>
    qc.invalidateQueries(
      { queryKey: trpc.workOrder.get.queryKey({ workOrderId }) },
      { throwOnError: true },
    );
  const create = useMutation(
    trpc.workOrder.taskCreate.mutationOptions({
      onSuccess: async (_result, request) => {
        if (!(await recovery.complete(request))) return;
        setName('');
        setOwner('');
        setDuration('');
        refresh();
      },
    }),
  );
  const update = useMutation(
    trpc.workOrder.taskUpdate.mutationOptions({
      retry: shouldRetryTaskEdit,
      onSuccess: refreshEdit,
    }),
  );
  const assignLine = useMutation(
    trpc.workOrder.assignLine.mutationOptions({
      retry: shouldRetryTaskEdit,
      onSuccess: refreshEdit,
    }),
  );
  const assignVisit = useMutation(
    trpc.workOrder.assignVisit.mutationOptions({ onSuccess: refresh }),
  );
  const unassignVisit = useMutation(
    trpc.workOrder.unassignVisit.mutationOptions({ onSuccess: refresh }),
  );
  const busy =
    !online ||
    q.isFetching ||
    q.isError ||
    editing ||
    create.isPending ||
    update.isPending ||
    assignLine.isPending ||
    assignVisit.isPending ||
    unassignVisit.isPending;
  const error =
    create.error ??
    update.error ??
    assignLine.error ??
    assignVisit.error ??
    unassignVisit.error;
  const state = shownState(q, q.data);
  const visitName = (id: string) => {
    const visit = visits.find((value) => value.id === id);
    return visit?.scheduledStartAt
      ? new Date(visit.scheduledStartAt).toLocaleString(undefined, {
          timeZone: timezone,
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : 'Unscheduled visit';
  };
  const crew = (members.data ?? []).filter((member) =>
    canHoldVisits(member.role as Role),
  );
  // A ref closes the same-render double-submit window across task and line editors.
  // Their mutations also await the authoritative refresh before releasing this lock.
  const saveEdit = async (write: () => Promise<unknown>) => {
    if (editLock.current || busy) return false;
    editLock.current = true;
    setEditing(true);
    try {
      await write();
      return true;
    } finally {
      editLock.current = false;
      setEditing(false);
    }
  };
  const reloadValues = async () => {
    const result = await q.refetch({ throwOnError: true });
    update.reset();
    assignLine.reset();
    return result.data;
  };
  const sendTask = async () => {
    if (!online || create.isPending || recovery.preparing) return;
    const request =
      recovery.read.status === 'ready'
        ? recovery.read.request
        : {
            workOrderId,
            name: name.trim(),
            assignedUserId: owner || undefined,
            estimatedDurationMinutes: duration ? Number(duration) : undefined,
            ...idem(),
          };
    if (await recovery.prepare(request)) create.mutate(request);
  };
  const createBlocked = busy || recovery.preparing || recovery.read.status !== 'empty';
  return (
    <>
      <SectionCard title="Tasks, labor and materials">
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Tasks organize the accepted scope across one or more visits. Assigning a task
            does not book a crew. Use a separate quote for additional priced scope.
          </p>
          <StaleNotice
            show={refreshFailed(q, q.data) || q.isPaused}
            onRetry={() => void q.refetch()}
          />
          {error ? (
            <Notice tone="danger" alert>
              {error.message}
            </Notice>
          ) : null}
          {mayEdit ? (
            <CommercialWriteRecovery
              read={recovery.read}
              error={recovery.error}
              pending={create.isPending || recovery.preparing}
              online={online}
              onRetry={() => void sendTask()}
              onResolve={() =>
                void recovery.resolveAttempt().then((resolved) => {
                  if (resolved) create.reset();
                })
              }
            />
          ) : null}
          {state !== 'ready' ? (
            <UnresolvedState
              state={state}
              icon={IconJobs}
              what="work order tasks"
              error={q.error}
              onRetry={() => void q.refetch()}
            />
          ) : q.data ? (
            <>
              {q.data.tasks.map((task) => (
                <section
                  key={task.id}
                  className="space-y-3 rounded-[var(--radius)] border border-border p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="mr-auto text-sm font-semibold">{task.name}</h3>
                    {mayEdit ? (
                      <InlineRename
                        value={task.name}
                        busy={busy}
                        onSave={(next) =>
                          void saveEdit(() =>
                            update.mutateAsync({
                              taskId: task.id,
                              expectedVersion: task.version,
                              name: next,
                              ...idem(),
                            }),
                          ).catch(() => {})
                        }
                      />
                    ) : null}
                  </div>
                  <TaskDetailsEditor
                    task={task}
                    crew={crew}
                    disabled={busy || !mayEdit}
                    mayEdit={mayEdit}
                    onSave={(input) => saveEdit(() => update.mutateAsync(input))}
                    onReload={reloadValues}
                  />
                  <p className="text-xs text-muted-foreground">
                    Planned across visits: {task.plannedDurationMinutes} min · Remaining
                    estimate:{' '}
                    {task.remainingDurationMinutes === null
                      ? 'Unknown'
                      : `${task.remainingDurationMinutes} min`}
                    {task.hasUnestimatedVisits
                      ? ' · Some visits have no planned duration'
                      : ''}
                  </p>
                  {task.planningOverrunMinutes > 0 ? (
                    <Notice tone="warn">
                      Planned visits exceed the task estimate by{' '}
                      {task.planningOverrunMinutes} minutes. The accepted quote remains
                      unchanged.
                    </Notice>
                  ) : null}
                  {mayEdit ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setAvailability({
                          kind: 'crew',
                          name: task.name,
                          userId: task.assignedUserId ?? undefined,
                          durationMinutes: task.estimatedDurationMinutes ?? undefined,
                        })
                      }
                    >
                      Check crew availability
                    </Button>
                  ) : null}
                  <ul className="divide-y divide-border">
                    {q.data.lines
                      .filter((line) => line.taskId === task.id)
                      .map((line) => (
                        <li key={line.id} className="space-y-2 py-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-medium">{line.description}</p>
                            <span className="text-xs text-muted-foreground">
                              {line.kind} · {line.qty} {line.unitLabel}
                            </span>
                          </div>
                          {mayEdit ? (
                            <div className="flex flex-wrap gap-2">
                              <LineTaskEditor
                                line={line}
                                tasks={q.data.tasks}
                                draft={lineDrafts[line.id] ?? null}
                                setDraft={(next) =>
                                  setLineDrafts((previous) => ({
                                    ...previous,
                                    [line.id]: next,
                                  }))
                                }
                                disabled={busy}
                                onSave={(input) =>
                                  saveEdit(() => assignLine.mutateAsync(input))
                                }
                                onReload={reloadValues}
                              />
                              {line.itemId ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setAvailability({
                                      kind: 'item',
                                      itemId: line.itemId!,
                                      name: line.description,
                                      locationId: line.sourceLocationId ?? undefined,
                                    })
                                  }
                                >
                                  Stock availability
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                          {mayEdit && line.kind === 'material' ? (
                            <MaterialTaskSplit
                              line={line}
                              tasks={q.data.tasks}
                              disabled={busy}
                              workOrderId={workOrderId}
                            />
                          ) : null}
                        </li>
                      ))}
                  </ul>
                  <h4 className="text-xs font-semibold">Visits for this task</h4>
                  <ul className="space-y-2">
                    {q.data.taskVisits
                      .filter((link) => link.taskId === task.id)
                      .map((link) => (
                        <li
                          key={link.id}
                          className="flex flex-wrap items-center justify-between gap-2 text-xs"
                        >
                          <span>
                            {visitName(link.visitId)}
                            {link.plannedDurationMinutes
                              ? ` · ${link.plannedDurationMinutes} minutes`
                              : ''}
                          </span>
                          {mayEdit ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busy}
                              onClick={() =>
                                unassignVisit.mutate({
                                  taskId: task.id,
                                  visitId: link.visitId,
                                  expectedVersion: task.version,
                                  ...idem(),
                                })
                              }
                            >
                              Remove task from visit
                            </Button>
                          ) : null}
                        </li>
                      ))}
                  </ul>
                  {mayEdit ? (
                    <form
                      className="flex flex-wrap items-end gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const visitId = chosenVisits[task.id];
                        if (!busy && visitId)
                          assignVisit.mutate({
                            taskId: task.id,
                            visitId,
                            expectedVersion: task.version,
                            plannedDurationMinutes: visitMinutes[task.id]
                              ? Number(visitMinutes[task.id])
                              : undefined,
                            ...idem(),
                          });
                      }}
                    >
                      <Field label="Add or update visit allocation">
                        <select
                          className={field}
                          value={chosenVisits[task.id] ?? ''}
                          disabled={busy}
                          onChange={(event) =>
                            setChosenVisits({
                              ...chosenVisits,
                              [task.id]: event.target.value,
                            })
                          }
                        >
                          <option value="">Choose a visit</option>
                          {visits
                            .filter(
                              (visit) =>
                                visit.status !== 'canceled' &&
                                visit.status !== 'completed' &&
                                visit.status !== 'no_show',
                            )
                            .map((visit) => (
                              <option key={visit.id} value={visit.id}>
                                {visitName(visit.id)}
                              </option>
                            ))}
                        </select>
                      </Field>
                      <Field label="Minutes on this visit">
                        <input
                          className={`${field} max-w-36`}
                          type="number"
                          min={1}
                          placeholder={
                            task.remainingDurationMinutes === null
                              ? 'Enter minutes'
                              : `${task.remainingDurationMinutes} remaining`
                          }
                          value={visitMinutes[task.id] ?? ''}
                          disabled={busy}
                          onChange={(event) =>
                            setVisitMinutes({
                              ...visitMinutes,
                              [task.id]: event.target.value,
                            })
                          }
                        />
                      </Field>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={busy || !chosenVisits[task.id]}
                      >
                        Save visit allocation
                      </Button>
                    </form>
                  ) : null}
                </section>
              ))}
              {q.data.tasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No named tasks yet.</p>
              ) : null}
              {mayEdit ? (
                <form
                  className="space-y-3 border-t border-border pt-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (createBlocked || !name.trim()) return;
                    void sendTask();
                  }}
                >
                  <fieldset
                    disabled={createBlocked}
                    className="grid gap-3 sm:grid-cols-3"
                  >
                    <Field label="New task">
                      <input
                        className={field}
                        value={name}
                        required
                        maxLength={200}
                        disabled={busy}
                        onChange={(event) => setName(event.target.value)}
                      />
                    </Field>
                    <Field label="Assignee">
                      <select
                        className={field}
                        value={owner}
                        disabled={busy}
                        onChange={(event) => setOwner(event.target.value)}
                      >
                        <option value="">Unassigned</option>
                        {crew.map((member) => (
                          <option key={member.userId} value={member.userId}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Estimated minutes">
                      <input
                        className={field}
                        type="number"
                        min={1}
                        value={duration}
                        disabled={busy}
                        onChange={(event) => setDuration(event.target.value)}
                      />
                    </Field>
                  </fieldset>
                  <Button type="submit" disabled={createBlocked || !name.trim()}>
                    Add task
                  </Button>
                </form>
              ) : null}
            </>
          ) : null}
          {availability ? (
            <AvailabilitySheet
              context={availability}
              onClose={() => setAvailability(null)}
            />
          ) : null}
        </div>
      </SectionCard>
      {state === 'ready' && q.data ? (
        q.data.materialScope === 'legacy' ? (
          <JobPartsCard jobId={workOrderId} closedReason={legacyClosedReason} />
        ) : q.data.materialScope === 'confirmed' ? (
          <WorkOrderBackordersCard workOrderId={workOrderId} />
        ) : null
      ) : null}
    </>
  );
}

type EditableTask = {
  id: string;
  version: number;
  status: string;
  assignedUserId: string | null;
  estimatedDurationMinutes: number | null;
};

function TaskDetailsEditor({
  task,
  crew,
  disabled,
  mayEdit,
  onSave,
  onReload,
}: {
  task: EditableTask;
  crew: { userId: string; name: string }[];
  disabled: boolean;
  mayEdit: boolean;
  onSave: (input: WorkOrderTaskUpdateInput) => Promise<boolean>;
  onReload: () => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<{
    base: EditableTask;
    status: EditableTask['status'];
    assignedUserId: string | null;
    minutes: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const lock = useRef(false);
  const [keys] = useState(() => createIntentKey());
  const values = draft ?? {
    ...task,
    minutes: String(task.estimatedDurationMinutes ?? ''),
  };
  const change = (
    patch: Partial<
      Pick<NonNullable<typeof draft>, 'status' | 'assignedUserId' | 'minutes'>
    >,
  ) => {
    setDraft((previous) => ({
      ...(previous ?? {
        base: task,
        status: task.status,
        assignedUserId: task.assignedUserId,
        minutes: String(task.estimatedDurationMinutes ?? ''),
      }),
      ...patch,
    }));
  };
  // Compare against the captured base, never silently rebase an unsaved draft on a
  // newer query result. Send only changed fields (a retired assignee may be unchanged).
  const changes: Partial<WorkOrderTaskUpdateInput> = {};
  if (draft) {
    if (draft.status !== draft.base.status)
      changes.status = draft.status as WorkOrderTaskUpdateInput['status'];
    if (draft.assignedUserId !== draft.base.assignedUserId)
      changes.assignedUserId = draft.assignedUserId;
    const minutes = draft.minutes === '' ? null : Number(draft.minutes);
    if (minutes !== draft.base.estimatedDurationMinutes)
      changes.estimatedDurationMinutes = minutes;
  }
  const changed = Object.keys(changes).length > 0;
  const stale = !!draft && draft.base.version !== task.version;
  const blocked = disabled || pending;
  const save = async () => {
    if (blocked || lock.current || !draft || !changed || stale) return;
    lock.current = true;
    setPending(true);
    setError(null);
    const input = { taskId: task.id, expectedVersion: draft.base.version, ...changes };
    try {
      if (await onSave({ ...input, ...keys.forIntent(['taskUpdate', input]) })) {
        setDraft(null);
        keys.clear();
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Task changes could not be saved.',
      );
    } finally {
      lock.current = false;
      setPending(false);
    }
  };
  const reload = async () => {
    if (blocked || lock.current) return;
    lock.current = true;
    setPending(true);
    try {
      await onReload();
      setDraft(null);
      setError(null);
      keys.clear();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Task values could not be refreshed.',
      );
    } finally {
      lock.current = false;
      setPending(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Task status">
          <select
            className={field}
            value={values.status}
            disabled={blocked}
            onChange={(event) =>
              change({ status: event.target.value as EditableTask['status'] })
            }
          >
            {WORK_ORDER_TASK_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Assigned to">
          <select
            className={field}
            value={values.assignedUserId ?? ''}
            disabled={blocked}
            onChange={(event) => change({ assignedUserId: event.target.value || null })}
          >
            <option value="">Unassigned</option>
            {values.assignedUserId &&
            !crew.some((member) => member.userId === values.assignedUserId) ? (
              <option value={values.assignedUserId}>Unavailable assignee</option>
            ) : null}
            {crew.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Estimated minutes">
          <input
            className={field}
            type="number"
            min={1}
            max={525600}
            value={values.minutes}
            disabled={blocked}
            onChange={(event) => change({ minutes: event.target.value })}
          />
        </Field>
      </div>
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
      {stale ? (
        <Notice tone="warn">
          This task changed on the server. Your unsaved changes are still here. Reload
          task values before editing the newer version.
        </Notice>
      ) : null}
      {mayEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={blocked || !changed || stale}
            onClick={() => void save()}
          >
            Save task changes
          </Button>
          {draft || error ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={blocked}
              onClick={() => void reload()}
            >
              Reload task values
            </Button>
          ) : null}
          {draft ? (
            <span className="text-xs text-muted-foreground">
              Reload replaces your unsaved changes.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

type LineTaskDraft = { taskId: string; baseTaskId: string | null; version: number };
function LineTaskEditor({
  line,
  tasks,
  draft,
  setDraft,
  disabled,
  onSave,
  onReload,
}: {
  line: { id: string; description: string; taskId: string | null; version: number };
  tasks: { id: string; name: string }[];
  draft: LineTaskDraft | null;
  setDraft: (next: LineTaskDraft | null) => void;
  disabled: boolean;
  onSave: (input: WorkOrderAssignLineInput) => Promise<boolean>;
  onReload: () => Promise<unknown>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const lock = useRef(false);
  const [keys] = useState(() => createIntentKey());
  const stale = !!draft && draft.version !== line.version;
  const changed = !!draft && draft.taskId !== draft.baseTaskId;
  const blocked = disabled || pending;
  const save = async () => {
    if (blocked || lock.current || !draft || !changed || stale) return;
    lock.current = true;
    setPending(true);
    setError(null);
    const input = {
      workOrderLineId: line.id,
      taskId: draft.taskId,
      expectedVersion: draft.version,
    };
    try {
      if (await onSave({ ...input, ...keys.forIntent(['assignLine', input]) })) {
        setDraft(null);
        keys.clear();
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Line assignment could not be saved.',
      );
    } finally {
      lock.current = false;
      setPending(false);
    }
  };
  const reload = async () => {
    if (blocked || lock.current) return;
    lock.current = true;
    setPending(true);
    try {
      await onReload();
      setDraft(null);
      setError(null);
      keys.clear();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Line assignment could not be refreshed.',
      );
    } finally {
      lock.current = false;
      setPending(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <select
          className={`${field} max-w-xs`}
          aria-label={`Task for ${line.description}`}
          value={draft?.taskId ?? line.taskId ?? ''}
          disabled={blocked}
          onChange={(event) => {
            const taskId = event.target.value;
            setDraft({
              ...(draft ?? { version: line.version, baseTaskId: line.taskId }),
              taskId,
            });
          }}
        >
          {tasks.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          disabled={blocked || !changed || stale}
          onClick={() => void save()}
        >
          Save line task
        </Button>
        {draft || error ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={blocked}
            onClick={() => void reload()}
          >
            Reload line assignment
          </Button>
        ) : null}
      </div>
      {error ? (
        <Notice tone="danger" alert>
          {error}
        </Notice>
      ) : null}
      {stale ? (
        <Notice tone="warn">
          This line changed on the server. Your unsaved assignment is still here. Reload
          the line assignment before editing the newer version.
        </Notice>
      ) : null}
      {draft ? (
        <p className="text-xs text-muted-foreground">
          Reload replaces your unsaved assignment.
        </p>
      ) : null}
    </div>
  );
}

function MaterialTaskSplit({
  line,
  tasks,
  disabled,
  workOrderId,
}: {
  line: {
    id: string;
    version: number;
    description: string;
    taskId: string | null;
    qty: string | number;
  };
  tasks: { id: string; name: string }[];
  disabled: boolean;
  workOrderId: string;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [taskId, setTaskId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [keys] = useState(() => createIntentKey());
  const split = useMutation(
    trpc.workOrder.splitLine.mutationOptions({
      onSuccess: () => {
        keys.clear();
        setQuantity('');
        setReason('');
        void qc.invalidateQueries({
          queryKey: trpc.workOrder.get.queryKey({ workOrderId }),
        });
        void qc.invalidateQueries({ queryKey: trpc.inventoryReservations.pathKey() });
      },
    }),
  );
  return (
    <details className="rounded-[var(--radius-sm)] border border-border px-3 text-xs">
      <summary className="min-h-11 cursor-pointer py-3 font-medium">
        Move part of this material to another task
      </summary>
      <form
        className="space-y-3 pb-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (disabled || split.isPending) return;
          const input = {
            workOrderLineId: line.id,
            taskId,
            quantityHundredths: qtyToHundredths(quantity),
            reason,
            expectedVersion: line.version,
          };
          split.mutate({ ...input, ...keys.forIntent([input]) });
        }}
      >
        <p className="text-muted-foreground">
          Partition the existing {line.qty} units. Used quantities stay on their original
          task. Price, tax and total accepted quantity stay unchanged. To move the entire
          line, use the task selector above.
        </p>
        {split.error ? (
          <Notice tone="danger" alert>
            {split.error.message}
          </Notice>
        ) : null}
        <fieldset
          disabled={disabled || split.isPending}
          className="grid gap-3 sm:grid-cols-2"
        >
          <Field label="Destination task">
            <select
              className={field}
              value={taskId}
              required
              onChange={(event) => setTaskId(event.target.value)}
            >
              <option value="">Choose task</option>
              {tasks
                .filter((task) => task.id !== line.taskId)
                .map((task) => (
                  <option key={task.id} value={task.id}>
                    {task.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Quantity to move">
            <input
              className={field}
              type="number"
              min="0.01"
              step="0.01"
              required
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
            />
          </Field>
          <Field label="Reason">
            <input
              className={field}
              value={reason}
              required
              maxLength={500}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        </fieldset>
        <Button
          type="submit"
          size="sm"
          disabled={disabled || split.isPending || !taskId || !quantity || !reason.trim()}
        >
          {split.isPending ? 'Moving allocation…' : 'Move material portion'}
        </Button>
      </form>
    </details>
  );
}
